import React, { useEffect, useState } from 'react';
import {
  Show,
  SimpleShowLayout,
  useRecordContext,
  useRedirect,
} from 'react-admin';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Avatar,
} from '@mui/material';
import {
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ResourceBreadcrumbs from '../../components/ResourceBreadcrumbs';
import { DriverTimeLogDialog } from '../../components/dialogs/DriverTimeLogDialog';

const formatDuration = (seconds: number): { display: string; detailed: string } => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  const displayParts = [];
  if (hours > 0) displayParts.push(`${hours}h`);
  if (minutes > 0) displayParts.push(`${minutes}m`);
  if (displayParts.length === 0) displayParts.push('0m');
  
  const detailedParts = [];
  if (hours > 0) detailedParts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0) detailedParts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  if (detailedParts.length === 0) detailedParts.push('0 minutes');
  
  return {
    display: displayParts.join(' '),
    detailed: detailedParts.join(', ')
  };
};


const InfoCard = ({ 
  icon, 
  title, 
  items 
}: { 
  icon: React.ReactNode;
  title: string;
  items: Array<{ label: string; value: React.ReactNode }>;
}) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="h6" fontWeight="medium">
            {title}
          </Typography>
        </Box>
        <Divider />
        <Stack spacing={2}>
          {items.map((item, index) => (
            <Box key={index}>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {item.label}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {item.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

const DriverTimeLogShowContent = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  
  if (!record) return null;
  
  const drivingDuration = formatDuration(record.durationDriving || 0);
  const onDutyDuration = formatDuration(record.durationOnDuty || 0);
  const offDutyDuration = formatDuration(record.durationOffDuty || 0);
  const personalConveyanceDuration = formatDuration(record.durationPersonalConveyance || 0);

  // Shift total = On Duty + Driving (per specs)
  const shiftTotalSeconds = (record.durationDriving || 0) + (record.durationOnDuty || 0);
  const shiftTotalDuration = formatDuration(shiftTotalSeconds);

  // Total time includes all states
  const totalSeconds = shiftTotalSeconds + (record.durationOffDuty || 0) + (record.durationPersonalConveyance || 0);

  const drivingPercentage = totalSeconds > 0
    ? Math.round((record.durationDriving / totalSeconds) * 100)
    : 0;
  const onDutyPercentage = totalSeconds > 0
    ? Math.round((record.durationOnDuty / totalSeconds) * 100)
    : 0;
  const offDutyPercentage = totalSeconds > 0
    ? Math.round(((record.durationOffDuty || 0) / totalSeconds) * 100)
    : 0;
  const personalConveyancePercentage = totalSeconds > 0
    ? Math.round(((record.durationPersonalConveyance || 0) / totalSeconds) * 100)
    : 0;
  
  const startDate = new Date(record.startedAt);
  const submitDate = new Date(record.submittedAt);
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <ResourceBreadcrumbs 
        customNames={{
          singular: 'Time Log',
          plural: 'Driver Time Logs'
        }}
      />
      
      {/* Header */}
      <Box sx={{ mb: 3, mt: 2 }}>
        <Typography variant="h4" fontWeight="bold">
          {record.user?.name || 'Unknown Driver'} - Time Log {startDate.toLocaleDateString()}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Driver Information Card */}
        <Grid item xs={12} md={6}>
          <InfoCard
            icon={
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                <PersonIcon fontSize="small" />
              </Avatar>
            }
            title="Driver Information"
            items={[
              { 
                label: 'Name', 
                value: record.user?.name || 'N/A' 
              },
              { 
                label: 'Email', 
                value: record.user?.email || 'N/A' 
              }
            ]}
          />
        </Grid>
        
        {/* Time Information Card */}
        <Grid item xs={12} md={6}>
          <InfoCard
            icon={
              <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                <CalendarIcon fontSize="small" />
              </Avatar>
            }
            title="Time Information"
            items={[
              { 
                label: 'Shift Started', 
                value: `${startDate.toLocaleDateString()}, ${startDate.toLocaleTimeString()}` 
              },
              { 
                label: 'Submitted', 
                value: `${submitDate.toLocaleDateString()}, ${submitDate.toLocaleTimeString()}` 
              }
            ]}
          />
        </Grid>
        
        {/* Time Summary Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={4}>
                {/* Shift Total - Key Number */}
                <Box sx={{
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                  pb: 3
                }}>
                  <Typography variant="overline" color="text.secondary" fontWeight="bold" letterSpacing={1}>
                    SHIFT TOTAL
                  </Typography>
                  <Typography variant="h2" fontWeight="bold" color="primary.main" sx={{ mt: 1 }}>
                    {shiftTotalDuration.display}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    On Duty + Driving
                  </Typography>
                </Box>

                {/* Time Breakdown List */}
                <Stack spacing={2.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight="medium">
                      ON DUTY
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {onDutyDuration.display}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight="medium">
                      DRIVING
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {drivingDuration.display}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight="medium">
                      OFF DUTY
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {offDutyDuration.display}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight="medium">
                      PERSONAL CONVEYANCE
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {personalConveyanceDuration.display}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Additional Information */}
        {record.notes && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {record.notes}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export const DriverTimeLogShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(true);

  const handleClose = () => {
    setDialogOpen(false);
    // Navigate back to the previous page or driver time logs list
    navigate('/driver-time-logs');
  };

  useEffect(() => {
    // Open the dialog when component mounts
    setDialogOpen(true);
  }, [id]);

  return (
    <DriverTimeLogDialog
      open={dialogOpen}
      onClose={handleClose}
      timeLogId={id ? parseInt(id) : null}
    />
  );
};